import { FC } from "react";
import { Smartphone } from "lucide-react";
import AudienceCell from "src/components/AudienceCell/AudienceCell";

type AudienceType = "everyone" | "platform" | "groups" | "users";
type AudiencePlatform = "ios" | "android" | "both";

export interface AnnouncementAudienceCellProps {
	audienceType: AudienceType;
	audiencePlatform?: AudiencePlatform | null;
	audienceUserIds?: string[];
	audienceGroupIds?: number[];
	groupNamesById: Map<number, string>;
}

/**
 * Audience column renderer for the Announcements admin list. Mirrors the
 * styling of the shared `AudienceCell` used by Events / Categories so the
 * tables read consistently, while extending coverage to the two
 * announcement-only modes (Platform, Specific Users) that `AudienceCell`
 * alone can't represent.
 *
 * - everyone → green "Everyone" pill (delegates to `AudienceCell`)
 * - groups   → one chip per group (delegates to `AudienceCell`)
 * - platform → neutral chip with smartphone icon (e.g. "iOS only")
 * - users    → neutral chip with user icon and count
 */
const AnnouncementAudienceCell: FC<AnnouncementAudienceCellProps> = ({
	audienceType,
	audiencePlatform,
	audienceUserIds,
	audienceGroupIds,
	groupNamesById
}) => {
	if (audienceType === "everyone") {
		return <AudienceCell groups={[]} />;
	}

	if (audienceType === "groups") {
		const ids = audienceGroupIds ?? [];
		// Unknown ids still render — the fallback "Group {id}" surfaces the
		// link so an admin can investigate stale references. If we ever want
		// to drop unknowns instead, filter on `groupNamesById.has(id)` before
		// the map.
		const resolved = ids.map((id) => ({
			id,
			name: groupNamesById.get(id) ?? `Group ${id}`
		}));
		return <AudienceCell groups={resolved} />;
	}

	if (audienceType === "platform") {
		const label =
			audiencePlatform === "ios"
				? "iOS only"
				: audiencePlatform === "android"
					? "Android only"
					: "Both platforms";
		return (
			<span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
				<Smartphone className="h-3 w-3" aria-hidden="true" />
				{label}
			</span>
		);
	}

	// users
	const count = audienceUserIds?.length ?? 0;
	return (
		<span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
			{count} user{count !== 1 ? "s" : ""}
		</span>
	);
};

export default AnnouncementAudienceCell;
